import type { DevPriceRowDto } from '@/api';
import type { NumericFilterValue } from '@/components/table/table-types';
import FilterDropdown from '@/components/table/toolbar/filter-dropdown';
import NumericColumnFilter from '@/components/table/toolbar/numeric-column-filter';
import { formatDateDisplay, formatDateTimeDisplay } from '@/utils/date-utils';
import { Box, Flex, Text } from '@lego/klik-ui';
import { ThemeProvider, createTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  MRT_ToggleFiltersButton,
  MaterialReactTable,
} from 'material-react-table';
import { useMemo, useState } from 'react';
import { formatPrice } from '../dev-dashboard-helpers';
import { type PriceRowsKind, useDevPriceRows } from '../dev-dashboard-queries';
import {
  type DateColumn,
  type ExactNumberColumn,
  type NumberColumn,
  PROCESSED_OPTIONS,
  countActiveFilters,
  getColumnOptions,
  toPriceRowFilter,
} from '../price-row-filters';
import { ActiveFilterStatus } from './active-filter-status';
import {
  DateRangeFilter,
  type DateRangeFilterValue,
  NumberInputFilter,
} from './price-row-column-filters';
import { StatusDot } from './status-dot';

type Props = {
  kind: PriceRowsKind;
  idHeader: string;
};

// A type alias (not an interface) so it fits the Record<string, unknown> row type of FilterStatus.
type PriceRow = Pick<DevPriceRowDto, keyof DevPriceRowDto>;

const theme = createTheme({ palette: { mode: 'light' } });

const EMPTY_FILTER = {};

const cellText = (text: string | number) => (
  <Text className="body-xs-regular" isTruncated>
    {text}
  </Text>
);

/**
 * The newest 50 price rows, with column filters and sorting (same filter components as the simulation
 * and create price tables). A filter loads the newest 50 rows that match it from the backend.
 * Sorting changes only the order of the loaded rows.
 */
export const FilterablePriceRowsTable = ({ kind, idHeader }: Props) => {
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );
  // Not memoized: material-react-table changes range filter values in place, so the state
  // array can keep its reference when a date changes.
  const filter = toPriceRowFilter(columnFilters);
  const activeFilterCount = countActiveFilters(filter);

  // The unfiltered rows give filter options that the current filter hides.
  const newestRows = useDevPriceRows(kind, EMPTY_FILTER);
  const filteredRows = useDevPriceRows(kind, filter);
  const rows: PriceRow[] = filteredRows.data ?? [];

  const optionSources = useMemo(
    () => [newestRows.data ?? [], filteredRows.data ?? []],
    [newestRows.data, filteredRows.data],
  );

  const numberColumn = (
    key: NumberColumn,
    header: string,
    format: (value: number) => string = String,
  ): MRT_ColumnDef<PriceRow> => ({
    accessorKey: key,
    header,
    sortingFn: 'basic',
    Cell: ({ row }) => cellText(format(row.original[key])),
    Filter: ({ header: columnHeader }) => (
      <NumericColumnFilter
        valueOptions={getColumnOptions(key, optionSources)}
        value={
          columnHeader.column.getFilterValue() as NumericFilterValue | undefined
        }
        onChange={(value) => columnHeader.column.setFilterValue(value)}
      />
    ),
  });

  // Number input; the backend returns rows with exactly this value.
  const exactNumberColumn = (
    key: ExactNumberColumn,
    header: string,
  ): MRT_ColumnDef<PriceRow> => ({
    accessorKey: key,
    header,
    sortingFn: 'basic',
    Cell: ({ row }) => cellText(row.original[key]),
    Filter: ({ header: columnHeader }) => (
      <NumberInputFilter
        value={columnHeader.column.getFilterValue() as string | undefined}
        onChange={(value) => columnHeader.column.setFilterValue(value)}
      />
    ),
  });

  // From/to date pickers. The filter components set the column filter directly, like the other
  // columns; the built-in material-react-table range fields change their value in place.
  const dateRangeColumn = (
    key: DateColumn | 'updatedAt',
    header: string,
    variant: 'date-range' | 'datetime-range',
    format: (value: Date) => string,
  ): MRT_ColumnDef<PriceRow> => ({
    id: key,
    accessorFn: (row) => row[key] ?? undefined,
    header,
    size: variant === 'datetime-range' ? 220 : 170,
    sortingFn: 'datetime',
    Cell: ({ row }) => {
      const value = row.original[key];
      return cellText(value ? format(value) : '-');
    },
    Filter: ({ header: columnHeader }) => (
      <DateRangeFilter
        value={
          columnHeader.column.getFilterValue() as
            | DateRangeFilterValue
            | undefined
        }
        onChange={(value) => columnHeader.column.setFilterValue(value)}
        withTime={variant === 'datetime-range'}
      />
    ),
  });

  const columns: MRT_ColumnDef<PriceRow>[] = [
    {
      id: 'isProcessed',
      accessorFn: (row) => (row.isProcessed ? 1 : 0),
      header: 'Is Processed',
      size: 110,
      sortingFn: 'basic',
      Cell: ({ row }) => (
        <StatusDot
          isOn={row.original.isProcessed}
          onLabel="Processed"
          offLabel="Not processed"
        />
      ),
      Filter: ({ header: columnHeader }) => (
        <FilterDropdown
          options={PROCESSED_OPTIONS}
          value={columnHeader.column.getFilterValue() as string[] | undefined}
          onChange={(selected) =>
            columnHeader.column.setFilterValue(
              selected.length > 0 ? selected : undefined,
            )
          }
          singleSelect
        />
      ),
    },
    numberColumn('id', idHeader),
    exactNumberColumn('year', 'Year'),
    exactNumberColumn('priceUnitId', 'Price Unit Id'),
    exactNumberColumn('productId', 'Product Id'),
    numberColumn('lp', 'LP', formatPrice),
    numberColumn('rrp', 'RRP', formatPrice),
    dateRangeColumn('validFrom', 'Valid From', 'date-range', formatDateDisplay),
    dateRangeColumn('validTo', 'Valid To', 'date-range', formatDateDisplay),
    dateRangeColumn(
      'updatedAt',
      'Updated At',
      'datetime-range',
      formatDateTimeDisplay,
    ),
  ];

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <ThemeProvider theme={theme}>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          dateFormats={{ keyboardDate: 'DD/MM/YYYY' }}
        >
          <MaterialReactTable
            columns={columns}
            data={rows}
            getRowId={(row) => String(row.id)}
            manualFiltering
            onColumnFiltersChange={setColumnFilters}
            state={{
              columnFilters,
              showProgressBars: filteredRows.isFetching,
            }}
            enableSorting
            enableStickyHeader
            enablePagination={false}
            enableColumnActions={false}
            enableGlobalFilter={false}
            enableBottomToolbar={false}
            enableColumnResizing
            layoutMode="grid"
            initialState={{ density: 'compact', showColumnFilters: true }}
            defaultColumn={{ size: 120, minSize: 70 }}
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              },
            }}
            muiTableContainerProps={{ sx: { flex: 1, minHeight: 0 } }}
            muiTableHeadCellProps={{
              sx: { fontSize: '11px', fontWeight: 600 },
            }}
            renderEmptyRowsFallback={() => (
              <Text className="body-xs-regular" padding={2}>
                {filteredRows.isError
                  ? 'Could not load the data. It reloads automatically.'
                  : 'No rows match the filters.'}
              </Text>
            )}
            renderTopToolbar={({ table }) => (
              <Flex marginY="4px" gap="8px" alignItems="center">
                <MRT_ToggleFiltersButton table={table} size="small" />
                <ActiveFilterStatus
                  count={activeFilterCount}
                  onClear={() => table.resetColumnFilters()}
                />
              </Flex>
            )}
          />
        </LocalizationProvider>
      </ThemeProvider>
    </Box>
  );
};
